import * as React from "react";
import { Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext.js";
import { LegalLinks } from "@/components/LegalLinks.js";
import type { OnboardingGoalOption } from "@homethyst/shared/schemas";
import { STRESS_SPACE_OPTIONS } from "@homethyst/shared/schemas";

const GOAL_ACK_LABELS: Record<OnboardingGoalOption, string> = {
  organize_home: "organize your home",
  track_stuff: "track your stuff",
  prepare_move: "prepare for your move",
  insurance_docs: "document for insurance",
  declutter: "declutter your space",
  find_things: "find things faster",
};

/** Join a short list into human prose: "A", "A and B", "A, B, and C". */
function humanList(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (el: HTMLElement, config: any) => void;
        };
      };
    };
  }
}

interface LoginPageProps {
  goals?: OnboardingGoalOption[];
  stressSpaces?: { room: string; reason: string }[];
  onBackToOnboarding?: () => void;
}

export default function LoginPage({ goals, stressSpaces, onBackToOnboarding }: LoginPageProps) {
  const { login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = React.useState<"login" | "register">("register");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState<{ email?: string; password?: string }>({});
  const [loading, setLoading] = React.useState(false);
  const googleBtnRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId || !window.google) return;
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response: { credential: string }) => {
        setError("");
        setLoading(true);
        try {
          await loginWithGoogle(response.credential, goals, stressSpaces);
        } catch (err: any) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      },
    });
    if (googleBtnRef.current) {
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "outline",
        size: "large",
        width: "100%",
        text: "continue_with",
      });
    }
  }, [loginWithGoogle]);

  const validate = (): boolean => {
    const errors: typeof fieldErrors = {};
    if (!email.trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errors.email = "Invalid email address";
    if (!password) errors.password = "Password is required";
    else if (mode === "register") {
      if (password.length < 8) errors.password = "Must be at least 8 characters";
      else if (!/[A-Z]/.test(password)) errors.password = "Must contain an uppercase letter";
      else if (!/\d/.test(password)) errors.password = "Must contain a number";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!validate()) return;
    setLoading(true);
    try {
      if (mode === "register") await register(email, password, goals, stressSpaces);
      else await login(email, password);
    } catch (err: any) {
      setError(err.message?.includes("401") || err.message?.includes("credentials") ? "Invalid email or password" : err.message ?? "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const clearFieldError = (field: keyof typeof fieldErrors) => setFieldErrors((p) => ({ ...p, [field]: undefined }));

  const pwChecks = [
    { label: "8+ characters", ok: password.length >= 8 },
    { label: "One uppercase letter", ok: /[A-Z]/.test(password) },
    { label: "One number", ok: /\d/.test(password) },
  ];

  // Acknowledge what the user told us during onboarding so their input visibly
  // matters, instead of dropping them onto a generic sign-up screen.
  const goalPhrase = (goals ?? []).length
    ? humanList((goals ?? []).map((g) => GOAL_ACK_LABELS[g]))
    : "";
  const stressSpaceNames = (stressSpaces ?? [])
    .map((s) => STRESS_SPACE_OPTIONS.find((o) => o.value === s.room)?.label ?? s.room);
  const showOnboardingAck = mode === "register" && (goalPhrase !== "" || stressSpaceNames.length > 0);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-neutral p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-neutral-border/30 p-8">
        <h1 className="text-2xl font-bold text-primary mb-1 font-headline">Homethyst</h1>
        <p className="text-sm text-gray-500 mb-4">Your calm home companion</p>

        {/* Onboarding acknowledgment — reflect back what the user told us */}
        {showOnboardingAck && (
          <div className="mb-5 rounded-lg border border-clay-border bg-amethyst-soft p-4">
            <div className="flex items-start gap-2.5">
              <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                {goalPhrase && <p>Let's create your account so we can help you <span className="font-medium text-gray-900">{goalPhrase}</span>.</p>}
                {stressSpaceNames.length > 0 && (
                  <p className={goalPhrase ? "mt-1.5" : ""}>
                    We'll start with the {humanList(stressSpaceNames)}.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mode toggle */}
        <div className="flex rounded-xl bg-gray-100 p-1 mb-5">
          <button onClick={() => { setMode("register"); setError(""); setFieldErrors({}); }} className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === "register" ? "bg-white text-primary shadow-sm" : "text-gray-500"}`}>
            Sign Up
          </button>
          <button onClick={() => { setMode("login"); setError(""); setFieldErrors({}); }} className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === "login" ? "bg-white text-primary shadow-sm" : "text-gray-500"}`}>
            Sign In
          </button>
        </div>

        <div ref={googleBtnRef} className="mb-4" />

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-neutral-border/40" />
          <span className="text-xs text-gray-500">or</span>
          <div className="flex-1 h-px bg-neutral-border/40" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-3" noValidate>
          <div>
            <input type="email" placeholder="Email" aria-label="Email" aria-invalid={!!fieldErrors.email} value={email} onChange={(e) => { setEmail(e.target.value); clearFieldError("email"); }} className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 ${fieldErrors.email ? "border-red-500" : "border-neutral-border/40"}`} />
            {fieldErrors.email && <p className="text-xs text-red-600 mt-1">{fieldErrors.email}</p>}
          </div>
          <div>
            <input type="password" placeholder="Password" aria-label="Password" aria-invalid={!!fieldErrors.password} value={password} onChange={(e) => { setPassword(e.target.value); clearFieldError("password"); }} className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 ${fieldErrors.password ? "border-red-500" : "border-neutral-border/40"}`} />
            {fieldErrors.password && <p className="text-xs text-red-600 mt-1">{fieldErrors.password}</p>}
          </div>
          {mode === "register" && (
            <div className="flex flex-col gap-0.5 -mt-1">
              {pwChecks.map((r) => (
                <span key={r.label} className={`text-xs ${r.ok ? "text-green-700" : "text-gray-500"}`}>
                  {r.ok ? "✓" : "○"} {r.label}
                </span>
              ))}
            </div>
          )}
          {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
          <button type="submit" disabled={loading} className="w-full py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-amethyst-ink disabled:opacity-50">
            {loading ? "..." : mode === "register" ? "Create Account" : "Sign In"}
          </button>
        </form>

        {mode === "login" && (
          <div className="mt-4 text-center">
            <a href="/forgot-password" className="text-xs text-gray-500 hover:text-gray-700">Forgot password?</a>
          </div>
        )}
        {onBackToOnboarding && (
          <div className="mt-2 text-center">
            <button onClick={onBackToOnboarding} className="text-xs text-gray-500 hover:text-gray-700">See intro again</button>
          </div>
        )}
      </div>

      <LegalLinks className="mt-6" />
    </div>
  );
}
